import React, { useCallback, useMemo, useState } from 'react';
import pluralize from 'pluralize';
import classnames from 'classnames';
import type { ColDef as ColumnDefinition } from 'ag-grid-community';

import AdminPageTabs from './adminPageTabs.component';
import { TabTypes } from './constants';
import HamburgerDropdown from './hamburgerDropdown.component';
import PeriodDropdown from '../../shared/periodDropdown.component';
import { PureAlert as Alert } from '../../shared/alert.component';
import NavigationPrompt, { IMPORTED_DATA_PENDING_MESSAGE } from '../../shared/navigationPrompt.component';
import { AlertType, Period } from '../../shared/types';

import SecondaryMenu from '../../menu/secondaryMenu.component';
import { useAddJob, useOnJimJobSuccess } from '../../shared/jim/hooks';
import { useSelector } from 'react-redux';
import {
  idOfLastJobOfTypeSelectorFactory, isLastJobOfTypeInProgressSelectorFactory,
  isLastJobOfTypeSucceededSelectorFactory,
  lastJobOfTypeSelectorFactory,
} from '../../shared/jim/store/selectors';
import JimJobInfo from '../../shared/jim/jimJobInfo.component';
import { JobConfig } from '../../shared/jim/propTypes';
import { useValidateJobResults } from '@queries/jobs';

type AdminImportTabsWithJimImportProps<
  TEntity,
  TFormData,
  TUploadedEntity = {},
  TAdditionalData = {},
> = {
  pageTitle: string,
  singularTitle?: boolean,

  canModify: boolean,

  hasPeriodControl?: boolean,
  period: Period,

  columnDefs: ColumnDefinition[],
  generateColumnDefs?: (columnDefs: ColumnDefinition[], additionalData?: TAdditionalData) => ColumnDefinition[],

  addEntityMessage: string,
  exportToExcel?: Function,
  generateTemplateFileName?: (options: { periodName: string }) => string,
  templatePath?: string,
  generateTemplate?: (callback: () => void) => void,
  disableGeneratingTemplate?: boolean,
  disableImporting?: boolean,
  disableExporting?: boolean,
  comparatorConfig?: {
    idField: string
    idFieldComparator?: (a: TEntity, b: TEntity) => boolean,
    ignoredFields?: string[],
    entitiesValuesComparatorFactory?: (additionalData?: TAdditionalData) =>
      (valA: TEntity, valB: TEntity, key: string) => boolean,
  },
  adminColumnRenderer?: React.ElementType,
  additionalHamburgerMenuItems?: (React.ReactNode)[] | null,

  AddEditEntityModal?: React.ElementType,
  RemoveEntityModal?: React.ElementType,
  showModalForAdd?: () => void,
  hideModalForAddEdit?: () => void,
  hideModalForRemove?: () => void,

  alert?: AlertType,
  hideAlert?: () => void,

  environmentSuffix: string,
  shouldDisplayZero: boolean,
  hideDecimals: boolean,
  negativeMinus: boolean,

  additionalData?: TAdditionalData,

  isFetchingEntities: boolean,
  isUploadingEntities?: boolean,
  isImportingEntities?: boolean,
  areImportsPending?: boolean,
  isRemovingEntity?: boolean,
  isSavingEntity?: boolean,

  entities: TEntity[],
  uploadedEntities?: TUploadedEntity[]


  fetchEntities: () => any | Promise<any>,
  saveEntity?: (
    formData: TFormData,
    openForEdit: boolean,
    options: { keepOpenAfterSaving: boolean, callback: () => void },
  ) => void,
  removeEntity?: (entity: TEntity) => void,
  uploadEntities?: (params: { file: File }) => void,
  importEntities?: (entities: any[], { onSuccess }: { onSuccess: () => void }) => void,

  entityToEdit?: TEntity | null,
  isAddEditModalVisible?: boolean,
  entityToRemove?: TEntity | null,
  isRemoveModalVisible?: boolean,
  rowToRemoveIndex?: number | null,

  className?: string,
  onGridReady?: () => void,

  validateJobConfig: JobConfig,
  importJobConfig: JobConfig,
}

function AdminImportTabsWithJimImport<
  TEntity extends object,
  TFormData,
  TUploadedEntity extends object = {},
  TAdditionalData = {},
>({
  canModify,
  AddEditEntityModal,
  RemoveEntityModal,
  addEntityMessage,
  columnDefs: baseColumnDefs,
  generateColumnDefs,

  exportToExcel,
  generateTemplateFileName,
  templatePath,
  generateTemplate,
  disableGeneratingTemplate,
  disableImporting,
  disableExporting,
  comparatorConfig,
  adminColumnRenderer,
  additionalHamburgerMenuItems,
  hasPeriodControl,
  singularTitle,

  pageTitle,

  alert,
  hideAlert,

  period,

  environmentSuffix,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,

  additionalData,

  fetchEntities,
  isFetchingEntities,

  entities,
  areImportsPending,
  isSavingEntity,
  isRemovingEntity,

  showModalForAdd,
  hideModalForRemove,
  hideModalForAddEdit,

  entityToEdit,
  entityToRemove,

  saveEntity,
  removeEntity,

  isAddEditModalVisible,
  isRemoveModalVisible,
  rowToRemoveIndex,

  className,

  validateJobConfig,
  importJobConfig,
}: AdminImportTabsWithJimImportProps<TEntity, TFormData, TUploadedEntity, TAdditionalData>) {
  const [activeTab, changeTab] = useState(TabTypes.VIEW);

  const idOfLastValidateJob = useSelector(useMemo(
    () => idOfLastJobOfTypeSelectorFactory(validateJobConfig.type),
    [validateJobConfig]
  ));

  const hasLastValidationSucceed = useSelector(useMemo(
    () => isLastJobOfTypeSucceededSelectorFactory(validateJobConfig.type),
    [validateJobConfig]
  ));

  const isValidationInProgress = useSelector(useMemo(
    () => isLastJobOfTypeInProgressSelectorFactory(validateJobConfig.type),
    [validateJobConfig]
  ));

  const lastImportJob = useSelector(useMemo(
    () => lastJobOfTypeSelectorFactory(importJobConfig.type),
    [importJobConfig]
  ));

  const isImportingEntities = useSelector(useMemo(
    () => isLastJobOfTypeInProgressSelectorFactory(importJobConfig.type),
    [importJobConfig]
  ));


  const {
    data: validatedEntities,
    isFetching: isFetchingValidationResults,
    refetch: refetchValidateJobResults,
  } = useValidateJobResults<TEntity>({
    jobId: idOfLastValidateJob,
    enabled: hasLastValidationSucceed,
  });

  const uploadedEntities = useMemo(
    () => hasLastValidationSucceed ? validatedEntities : [],
    [hasLastValidationSucceed, validatedEntities]
  );

  const validateEntitiesJob = useAddJob({
    jobType: validateJobConfig.type,
  });

  const importEntitiesJob = useAddJob({
    jobType: importJobConfig.type,
  });

  const isUploadingEntities = isFetchingValidationResults || isValidationInProgress;

  const uploadEntities = useCallback(({ file } : { file: File }) => {
    validateEntitiesJob({
      file,
      periodName: period.name,
    });
  }, [period.name, validateEntitiesJob]);

  const importEntities = useCallback(
    async () => {
      await importEntitiesJob({
        jobId: idOfLastValidateJob,
        periodName: period.name,
      });
    },
    [importEntitiesJob, idOfLastValidateJob, period.name]
  );

  useOnJimJobSuccess(lastImportJob, async () => {
    changeTab(TabTypes.VIEW);
    await Promise.all([
      refetchValidateJobResults(),
      fetchEntities(),
    ]);
  });


  const fileToImportChange = useCallback(event => {
    if (uploadEntities) {
      uploadEntities({ file: event.target.files[0] });
    }
  }, [uploadEntities]);

  const fileToImportSelect = useCallback(hideDropDown => () => {
    changeTab(TabTypes.IMPORT);
    hideDropDown();
  }, []);

  const templateFileName = generateTemplateFileName && period
    ? `${generateTemplateFileName({ periodName: period.name })}${environmentSuffix}.xlsx`
    : null;

  const selectFileToUpload = useCallback(file => {
    if (uploadEntities) {
      uploadEntities({ file });
    }
  }, [uploadEntities]);

  const columnDefs = generateColumnDefs
    ? generateColumnDefs(baseColumnDefs, additionalData)
    : baseColumnDefs;

  const exportToExcelHandler = useCallback(callback => {
    if (exportToExcel && period) {
      exportToExcel({
        entities,
        periodName: period.name,
        environmentSuffix,
        shouldDisplayZero,
        hideDecimals,
        negativeMinus,
        columnDefs,
        additionalData,
      });
      callback();
    }
  }, [
    entities, period, environmentSuffix, shouldDisplayZero,
    hideDecimals, negativeMinus, columnDefs, exportToExcel, additionalData,
  ]);

  const title = singularTitle ? pageTitle : pluralize.plural(pageTitle);

  const importEntitiesHandler = useCallback(async () => {
    importEntities?.();
  }, [importEntities]);

  const adminImportTabClassNames = classnames(
    'admin-import-tab',
    className
  );

  return (
    <div className={adminImportTabClassNames}>
      <NavigationPrompt
        show={Boolean(areImportsPending)}
        message={<span>{IMPORTED_DATA_PENDING_MESSAGE}</span>}
      />
      <SecondaryMenu>
        {hasPeriodControl ? <PeriodDropdown /> : null}
        <div className="hamburger-dropdown ml-auto text-right">
          <HamburgerDropdown
            canModify={canModify}
            showModalForAdd={showModalForAdd}
            addEntityMessage={addEntityMessage}
            fileToImportChange={fileToImportChange}
            fileToImportSelect={fileToImportSelect}
            templateFileName={templateFileName}
            templatePath={templatePath}
            generateTemplate={generateTemplate}
            disableGeneratingTemplate={disableGeneratingTemplate}
            disableExporting={disableExporting}
            disableImporting={disableImporting}
            exportToExcel={exportToExcel && exportToExcelHandler}
            additionalItems={additionalHamburgerMenuItems}
          />
        </div>
      </SecondaryMenu>
      <div className="page">
        <div className="container-fluid">
          {alert && <Alert content={alert.content} type={alert.alertType} hideAlert={hideAlert} show={alert.show}/>}
          <div className="row no-gutters page-content-container">
            <div className="col">
              <div className="d-flex flex-row justify-content-between">
                <h5>{title}</h5>
              </div>
            </div>
          </div>
          <div className="row no-gutters page-content-container">
            <div className="col">

              <AdminPageTabs
                canModify={canModify}
                pageTitle={pageTitle}
                activeTab={activeTab}
                changeTab={changeTab}
                columnDefs={columnDefs as any}
                isFetchingEntities={isFetchingEntities}
                entities={entities}
                selectFileToUpload={selectFileToUpload}
                uploadedEntities={uploadedEntities || []}
                uploadingEntities={Boolean(isUploadingEntities || isImportingEntities)}
                importEntities={importEntitiesHandler}
                comparatorConfig={comparatorConfig as any}
                adminColumnRenderer={adminColumnRenderer}
                templateFileName={templateFileName}
                templatePath={templatePath}
                generateTemplate={generateTemplate}
                disableGeneratingTemplate={disableGeneratingTemplate}
                disableImporting={disableImporting}
                additionalData={additionalData}
                importProgressBarComponent={
                  <>
                    <JimJobInfo
                      jobConfig={validateJobConfig}
                      showProgressBar
                    />
                    <JimJobInfo
                      jobConfig={importJobConfig}
                      showProgressBar
                    />
                  </>
                }
              />
            </div>
          </div>
        </div>
      </div>
      {AddEditEntityModal && canModify && <AddEditEntityModal
        entityToEdit={entityToEdit}
        hideModal={hideModalForAddEdit}
        saveEntity={saveEntity}
        isSavingEntity={isSavingEntity}
        period={period}
        isVisible={isAddEditModalVisible}
        openForEdit={Boolean(isAddEditModalVisible && entityToEdit)}
        additionalData={additionalData}
      />}
      {RemoveEntityModal && canModify && <RemoveEntityModal
        entityToRemove={entityToRemove}
        rowIndex={rowToRemoveIndex}
        isVisible={isRemoveModalVisible}
        hideModal={hideModalForRemove}
        removeEntity={removeEntity}
        isRemovingEntity={isRemovingEntity}
        period={period}
        additionalData={additionalData}
      />}
    </div>
  );
}

AdminImportTabsWithJimImport.defaultProps = {
  hasPeriodControl: true,
};

export default AdminImportTabsWithJimImport;
